<template>
  <div>
    <LibInactiveLocaleMessage :is-locale-active="isLocaleActive" />

    <search-page-layout
      class="theme-dark"
      :search-dropdown-is-open="searchDropdownIsOpen"
      :show-results="showResults && searchQuery.trim().length > 0"
    >
      <template #breadcrumb>
        <Breadcrumb
          :items="breadCrumbItems"
          :show-transparent-background="false"
        />
      </template>
      <template #search-box>
        <search-box
          ref="searchBoxRef"
          v-bind="searchBoxArgs"
          v-model:search-query="autocompleteQuery"
          :suggestion-groups="suggestionGroups"
          @submit="onSubmit"
          @delete-suggestion="onDeleteSuggestions"
        />
      </template>
      <template #initial-content>
        <tag-list-group
          v-bind="tagListGroupArgs"
          @tag-close="onDeleteSuggestions"
        />
      </template>
      <template #search-content>
        <span id="globalSearchPageContainer" />
        <tabs-component
          :state="externalState"
          title="Search results"
          tab-list-position="top"
        >
          <tab-panel
            :title="TABS[0].label"
            :active-on-init="activeTabType === TABS[0].type"
            :badge="{ text: productsBadgeCount }"
            :uid="TABS[0].panelId"
          >
            <product-search
              :show-filter="true"
              :results-text="`${getResultsRange(TabType.PRODUCTS)}`"
            />
          </tab-panel>
          <tab-panel
            :title="TABS[1].label"
            :active-on-init="activeTabType === TABS[1].type"
            :badge="{ text: softwareBadgeCount }"
            :uid="TABS[1].panelId"
          >
            <SoftwareCenter />
          </tab-panel>
          <tab-panel
            :title="TABS[2].label"
            :active-on-init="activeTabType === TABS[2].type"
            :badge="{ text: documentsBadgeCount }"
            :uid="TABS[2].panelId"
          >
            <document-center-list
              :abort-function="onDownloadCenterAbort"
              :after-close-function="onDownloadCenterAfterClose"
            />
          </tab-panel>
          <tab-panel
            :title="TABS[3].label"
            :active-on-init="activeTabType === TABS[3].type"
            :badge="{ text: resourcesBadgeCount }"
            :uid="TABS[3].panelId"
          >
            <!-- Show loading skeleton when search is in progress but no response yet -->
            <ResultsSkeleton
              v-if="productListIsLoading && !searchResponse"
            ></ResultsSkeleton>

            <product-list-layout
              v-if="hasResourcesResults || productListIsLoading"
              :show-filter="hasResourcesResults"
              :results-text="
                hasResourcesResults
                  ? `${getResultsRange(TabType.RESOURCES)}`
                  : ''
              "
              :is-loading="productListIsLoading"
              :content-heading-id="
                $textDictionary['searchPage.list.contentHeadingId']
              "
              :content-heading="
                $textDictionary['searchPage.list.contentHeading']
              "
              :filters-skip-btn-text="
                $textDictionary['searchPage.filter.filtersSkipBtnText.label']
              "
              :filters-open-btn-text="
                $textDictionary['searchPage.filter.filtersOpenBtnText.label']
              "
              :filters-modal-title="
                $textDictionary[
                  'searchPage.filterModal.filtersModalTitle.label'
                ]
              "
              :filters-modal-close-icon-text="
                $textDictionary[
                  'searchPage.filterModal.filtersModalCloseIconText.label'
                ]
              "
              :filters-modal-apply-btn-text="
                $textDictionary[
                  'searchPage.filterModal.filtersModalApplyBtnText.label'
                ]
              "
              :data-insights-index="resourcesIndexName"
              :data-insights-query-id="resourcesQueryId"
            >
              <template #filter>
                <ResourcesFilter
                  v-if="hasResourcesResults"
                  :tags="tags.resources"
                  :search-response="searchResponse"
                />
              </template>
              <template #filterTags>
                <TagList
                  v-if="hasResourcesResults && resourcesStore.hasActiveFilters"
                  :tags="formattedResourceFilterTags"
                  :is-small="true"
                  style-type="secondary"
                  @click="handleResourceTagClick"
                  @close="handleResourceTagClick"
                />
              </template>
              <template #list>
                <Results
                  v-if="hasResults && !productListIsLoading"
                  :results="filteredResourcesResults"
                  :allowed-tags="currentAllowedTags"
                />
                <ResultsSkeleton v-if="productListIsLoading"></ResultsSkeleton>
              </template>
            </product-list-layout>
            <!-- Pagination component -->
            <div
              v-if="
                hasResults &&
                !productListIsLoading &&
                getTotalPages(activeType) > 1
              "
              class="pagination-container"
            >
              <GlobalSearchPagination
                :current-page="getCurrentPage()"
                :total-pages="getTotalPages(activeType)"
                :prev-label="
                  $textDictionary['searchPage.pagination.prev.label']
                "
                :next-label="
                  $textDictionary['searchPage.pagination.next.label']
                "
                @page-change="handlePageChange"
              />
            </div>
            <!-- Only show no results when search is complete AND there are no results -->
            <div
              v-if="!hasResults && !productListIsLoading && searchResponse"
              class="search-page-layout__empty-content"
            >
              <div class="search-page-layout__empty-content-text rte-text">
                <LibRichTextRenderer :rich-text="noResultsRichText" />
              </div>
              <tag-list-group
                v-bind="{
                  headlineLevel: 4,
                  items: [tagListGroupArgs.items[2]]
                }"
              />
            </div>
          </tab-panel>
        </tabs-component>
      </template>
    </search-page-layout>
  </div>
</template>

<script setup lang="ts">
import type { ITechnicalComponent } from '~/lib/ContentfulService';
import Breadcrumb from '~/components/global/lib/Breadcrumb.vue';
import { buildUrlString } from '~/utils/buildUrlString';
import {
  ProductListLayout,
  SearchBox,
  SearchPageLayout,
  TabPanel,
  TabsComponent,
  TagList,
  TagListGroup
} from '@hypercodestudio/basler-components';
import { useSearchSuggestions } from '~/composables/useSearchSuggestions';
import type { NavigationItemInterface } from '@hypercodestudio/basler-components/dist/models/navigation/NavigationItem';
import type { TabData } from '@hypercodestudio/basler-components/dist/models/tabs';
import { useSearchHistoryStore } from '~/stores/globalSearch/searchHistory';
import { useProductsStore } from '~/stores/productsStore/productsStore';
import {
  ALLOWED_RESOURCE_TAGS,
  useResourcesStore
} from '~/stores/globalSearch/resourcesStore';
import ProductSearch from '~/components/global/lib/Search/ProductSearch.vue';
import { useGlobalSearch } from '~/composables/useGlobalSearch';
import { PRODUCTS_TAG } from '~/lib/AlgoliaService/constants';
import Results from '~/components/global/lib/Search/Results.vue';
import type { SearchTagModel } from '~/utils/createDictKeysForSearchTags';
import { createSearchTags } from '~/utils/createDictKeysForSearchTags';
import ResultsSkeleton from '~/components/global/lib/Search/ResultsSkeleton.vue';
import GlobalSearchPagination from '~/components/global/lib/Search/GlobalSearchPagination.vue';
import SoftwareCenter from '~/components/global/lib/Search/SoftwareCenter.vue';
import DocumentCenterList from '~/components/global/lib/Search/DocumentCenterList.vue';
import { useDownloadStore as useSoftwareDownloadStore } from '~/stores/downloadStore/softwareDownloadStore';
import { useDownloadStore } from '~/stores/downloadStore/downloadStore';
import { debounce } from '~/composables/useDebounce';
import ResourcesFilter from '~/components/global/lib/Search/ResourcesFilter.vue';
import type { TagInterface } from '@hypercodestudio/basler-components/dist/components/elements/taglist/TagList.vue';

interface Props {
  isLocaleActive: boolean;
}
defineProps<Props>();

const MIN_AUTOCOMPLETE_LENGTH = 3;
const ITEMS_PER_PAGE = 9; // Default items per page from the API

const locale = useRouteParamLocale();
const route = useRoute();
const { $globalPageSettings, $dictionary, $textDictionary } = useNuxtApp();
const softwareDownloadStore = useSoftwareDownloadStore();
const softwareStore = useDownloadStore();
const searchHistoryStore = useSearchHistoryStore();
const productsStore = useProductsStore();
const resourcesStore = useResourcesStore();

const currentPage = ref(parseInt(route.query.page?.toString() || '1', 10));

const onDownloadCenterAbort = () => {
  softwareStore.closeModal();
};

const onDownloadCenterAfterClose = () => {};

/**
 * Get the current page for a specific tag type
 * @param tag The tag type (PRODUCTS_TAG, DOCUMENTS_TAG, etc.)
 * @returns The current page number
 */
function getCurrentPage(): number {
  return currentPage.value;
}

/**
 * Get the total number of pages for a specific tag type
 * @param tag The tag type (PRODUCTS_TAG, DOCUMENTS_TAG, etc.)
 * @returns The total number of pages
 */
function getTotalPages(tag: string | null): number {
  if (!tag) {
    return 0;
  }
  if (!searchResponse.value?.totalCounts) {
    return 1;
  }

  const { totalCounts } = searchResponse.value;
  let totalItems = 0;

  // Get the total count based on the tag type
  // Since we only have contentPages now, use that for all tags
  totalItems = totalCounts.contentPages;

  // Calculate total pages (ceiling division to ensure we have enough pages)
  return Math.ceil(totalItems / ITEMS_PER_PAGE);
}

/**
 * Calculate the range of results being displayed based on current page and items per page
 * @param tag The tag type (TabType enum or string)
 * @returns A string representing the range using the dictionary template
 */
function getResultsRange(tag: TabType | string): string {
  let templateKey = 'searchPage.resultsRange.label';
  // Special handling for Products tab
  if (tag === TabType.PRODUCTS) {
    templateKey = 'productsList.resultsHint';
    if (!productsStore.products) {
      return '0-0';
    }

    const totalItems = productsStore.products.maxCount;
    // Calculate start and end item numbers for Products
    const startItem = (currentPage.value - 1) * ITEMS_PER_PAGE + 1;
    const endItem = Math.min(startItem + ITEMS_PER_PAGE - 1, totalItems);

    // If there are no results, show 0-0
    if (totalItems === 0) {
      return '0-0';
    }

    return (
      $textDictionary.value[templateKey]
        ?.replace('{from}', String(startItem))
        .replace('{to}', String(endItem))
        .replace('{total}', String(totalItems)) ??
      `${startItem}-${endItem} / ${totalItems}`
    );
  }

  // Standard handling for other tabs
  if (!searchResponse.value?.totalCounts) {
    return '0-0';
  }

  const { totalCounts } = searchResponse.value;
  let totalItems = 0;

  // Get the total count based on the tag type
  // Since we only have contentPages now, use that for all tags
  totalItems = totalCounts.contentPages;

  // Calculate start and end item numbers
  const startItem = (currentPage.value - 1) * ITEMS_PER_PAGE + 1;
  let endItem = startItem + ITEMS_PER_PAGE - 1;

  // Make sure end item doesn't exceed total items
  endItem = Math.min(endItem, totalItems);

  // If there are no results, show 0-0
  if (totalItems === 0) {
    return '0-0';
  }

  return (
    $textDictionary.value[templateKey]
      ?.replace('{start}', String(startItem))
      .replace('{count}', String(endItem))
      .replace('{total}', String(totalItems)) ??
    `${startItem}-${endItem} / ${totalItems}`
  );
}

// TODO: fix this once we are a Technical Component
// const configuration = computed<UseGlobalSearchOptions>(
//   () => props.item.fields.configuration ?? {}
// );
const configuration = {};
const {
  fetchResults,
  searchQuery,
  searchResponse,
  autocompleteQuery,
  activeType,
  hasResults,
  responseCache,
  indexName: resourcesIndexName,
  queryId: resourcesQueryId
  // resultsText
} = useGlobalSearch(configuration as any);

// Dedicated reactive state for resources search response
const resourcesSearchResponse = ref<any>(null);

/**
 * Fetch resources search results and return the response
 * @param query The search query
 * @param facetFilters The facet filters object
 * @param offset The pagination offset (optional)
 */
async function fetchResourcesResults(
  query: string,
  facetFilters: Record<string, string[]> = {},
  offset: number = 0
) {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return null;
  }
  try {
    // Direct API call for resources with facet filters
    return await $fetch<any>('/api/algolia/search', {
      method: 'POST',
      body: {
        query: trimmedQuery,
        facetFilters,
        hitsPerPage: ITEMS_PER_PAGE,
        offset,
        locale: locale.value
      }
    });
  } catch (e) {
    console.error('Error fetching resources results:', e);
    return null;
  }
}

/**
 * Fetch search results with pagination for resources
 * @param query The search query
 * @param facetFilters The facet filters object
 * @param offset The pagination offset
 */
async function fetchResultsWithPagination(
  query: string,
  facetFilters: Record<string, string[]> = {},
  offset: number
) {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return;
  }
  try {
    // Direct API call with offset parameter and facet filters
    searchResponse.value = await $fetch<any>('/api/algolia/search', {
      method: 'POST',
      body: {
        query: trimmedQuery,
        facetFilters,
        hitsPerPage: ITEMS_PER_PAGE,
        offset,
        locale: locale.value
      }
    });
    resourcesIndexName.value = searchResponse.value?.indexName ?? '';
    resourcesQueryId.value = searchResponse.value?.queryId ?? '';
  } catch (e) {
    console.error('Error fetching paginated results:', e);
  }
}

// Define the structure of the tags object
interface TagsMap {
  [key: string]: SearchTagModel[];
}

const noResultsRichText =
  $dictionary.value['searchPage.resources.noResults.label'];

const tags = computed<TagsMap>(() => {
  return {
    [PRODUCTS_TAG]: [
      {
        value: PRODUCTS_TAG,
        label:
          $textDictionary.value['searchPage.tabs.products.label'] || 'Products'
      }
    ] satisfies SearchTagModel[],
    software: [
      {
        value: 'software',
        label:
          $textDictionary.value['searchPage.tabs.software.label'] || 'Software'
      }
    ] satisfies SearchTagModel[],
    documents: [
      {
        value: 'documents',
        label:
          $textDictionary.value['searchPage.tabs.documents.label'] ||
          'Documents'
      }
    ] satisfies SearchTagModel[],
    resources:
      resourcesSearchResponse.value?.filters?.map(
        (filter: {
          name: string;
          values: { value: string; count: number }[];
        }) => {
          return {
            value: filter.name,
            label:
              $textDictionary.value['searchPage.tabs.resources.label'] ||
              'Resources'
          };
        }
      ) || createSearchTags([], $textDictionary.value, locale.value)
  };
});

/**
 * Tab type enum - used for URL query parameters and type identification
 */
enum TabType {
  PRODUCTS = 'products',
  SOFTWARE = 'software',
  DOCUMENTS = 'documents',
  RESOURCES = 'resources'
}

enum TabId {
  PRODUCTS = 0,
  SOFTWARE = 1,
  DOCUMENTS = 2,
  RESOURCES = 3
}

/**
 * Tab definition interface
 */
interface Tab {
  id: string; // Unique identifier for the tab
  type: TabType; // Type identifier (used in URL)
  label: string; // Display label
  panelId: string; // ID for the tab panel
}

/**
 * Clean definition of all search tabs
 */
const TABS: Tab[] = [
  {
    id: String(TabId.PRODUCTS),
    type: TabType.PRODUCTS,
    label:
      $textDictionary.value['searchPage.tabs.products.label'] || 'Products1',
    panelId: 'search-tab-panel-0'
  },
  {
    id: String(TabId.SOFTWARE),
    type: TabType.SOFTWARE,
    label:
      $textDictionary.value['searchPage.tabs.software.label'] || 'Software',
    panelId: 'search-tab-panel-1'
  },
  {
    id: String(TabId.DOCUMENTS),
    type: TabType.DOCUMENTS,
    label:
      $textDictionary.value['searchPage.tabs.documents.label'] || 'Documents',
    panelId: 'search-tab-panel-2'
  },
  {
    id: String(TabId.RESOURCES),
    type: TabType.RESOURCES,
    label:
      $textDictionary.value['searchPage.tabs.resources.label'] || 'Resources',
    panelId: 'search-tab-panel-3'
  }
];

const activeTabNumber = ref(0);
const currentTag = ref(TabType.PRODUCTS);

const currentAllowedTags = computed(() => {
  if (Object.keys(tags.value).includes(currentTag.value)) {
    return (tags.value as any)[currentTag.value];
  }

  return [];
});

const breadCrumbItems = computed<NavigationItemInterface[]>(() => {
  // Get the current search query from the route
  const currentQuery = route.query.q?.toString() || '';

  const items = [
    {
      text: 'Home',
      link: { uri: buildUrlString(locale.value) }
    },
    {
      text:
        $textDictionary.value['searchPage.breadcrumb.search.label'] ?? 'Search',
      link: $globalPageSettings.value?.searchResultsPage?.slug
        ? {
            uri: buildUrlString(
              locale.value,
              $globalPageSettings.value?.searchResultsPage?.slug,
              '',
              undefined,
              true
            )
          }
        : {}
    }
  ];

  // Add a third breadcrumb item for search results when there's a query
  if (currentQuery) {
    items.push({
      text:
        ($textDictionary.value['searchPage.breadcrumb.searchresults.label'] ??
          'Results for ') + ` '${currentQuery}'`,
      link: $globalPageSettings.value?.searchResultsPage?.slug
        ? {
            uri: buildUrlString(
              locale.value,
              $globalPageSettings.value?.searchResultsPage?.slug,
              `q=${currentQuery}`,
              undefined,
              true
            )
          }
        : {}
    });
  }

  return items;
});

const getResultsByTag = () => {
  return resourcesSearchResponse.value.results['contentPages'] || [];
};

/**
 * Handle page change event from pagination component
 * @param page The new page number
 */
async function handlePageChange(page: number) {
  // Validate page number
  if (page < 1 || !searchQuery.value.trim()) {
    return;
  }

  // Update the current page
  currentPage.value = page;

  // Update the URL with the new page number
  // Keep the current tab (type) and other query parameters
  await router.push({
    query: {
      ...route.query,
      page: page.toString()
    }
  });

  // Scroll to top of results
  const container = document.getElementById('globalSearchPageContainer');
  if (container) {
    container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // For Resources tab, we need to manually fetch results with pagination
  if (currentTag.value === TabType.RESOURCES && searchQuery.value.trim()) {
    // Show loading state with minimum time
    startLoadingWithMinTime();

    // Calculate offset based on current page
    const offset = (page - 1) * ITEMS_PER_PAGE;

    // Build facet filters from active resource filters
    const facetFilters = resourcesStore.buildFacetFilters();

    // Fetch results with the facet filters and calculated offset
    await fetchResultsWithPagination(searchQuery.value, facetFilters, offset);

    // Update the dedicated resources search response with the current searchResponse
    resourcesSearchResponse.value = searchResponse.value;

    // Hide loading state with minimum time enforcement
    await stopLoadingWithMinTime();
  }

  // For other tabs, the route watcher will handle the page change
}

const externalState = reactive({
  title: 'Tabs Text Media Block',
  tabs: [] as TabData[]
});

// watch externalState
watch(externalState, (newVal) => {
  activeTabNumber.value = newVal.tabs.findIndex((tab: TabData) => tab.isActive);
});

// Update Badge Numbers in Tabs and Available Facets
// Watch the dedicated resources search response for resources-specific updates
watch(resourcesSearchResponse, () => {
  if (resourcesSearchResponse?.value?.totalCounts) {
    if (externalState.tabs.length === 0) {
      return;
    }
    if (
      externalState.tabs.length > TabId.RESOURCES &&
      externalState.tabs[TabId.RESOURCES]?.badge
    ) {
      externalState.tabs[TabId.RESOURCES].badge.text =
        resourcesBadgeCount.value;
    }
  }

  // Update available filters in the resources store
  if (resourcesSearchResponse?.value?.filters) {
    resourcesStore.updateAvailableFilters(
      resourcesSearchResponse.value.filters
    );
  }
});

watch(
  () => softwareDownloadStore.softwareDownloads,
  () => {
    if (
      externalState.tabs.length > TabId.SOFTWARE &&
      externalState.tabs[TabId.SOFTWARE]?.badge
    ) {
      externalState.tabs[TabId.SOFTWARE].badge.text = String(
        softwareDownloadStore.softwareDownloads?.maxCount
      );
    }
  }
);

watch(
  () => softwareStore.downloads,
  () => {
    if (
      externalState.tabs.length > TabId.DOCUMENTS &&
      externalState.tabs[TabId.DOCUMENTS]?.badge
    ) {
      let maxCount = 0;
      if (softwareStore.downloads?.results) {
        for (const key in softwareStore.downloads.results) {
          maxCount = maxCount + softwareStore.downloads.results[key].maxCount;
        }
      }
      externalState.tabs[TabId.DOCUMENTS].badge.text = String(maxCount);
    }
  }
);

// Flag to prevent duplicate searches when route handler is already processing
const isRouteChangeInProgress = ref(false);

/**
 * Watch for active tab changes and update the route query parameter
 */
watch(activeTabNumber, (newVal) => {
  // Skip if route change is already being handled by handleRouteQueryChange
  if (isRouteChangeInProgress.value) {
    return;
  }

  // Only update URL if we have a search query
  if (!searchQuery.value.trim()) {
    return;
  }

  // Get the selected tab from our tab definitions
  const selectedTab = TABS[newVal];
  if (selectedTab) {
    // Update current tag and active type based on the selected tab
    currentTag.value = selectedTab.type;
    activeType.value = selectedTab.type;

    // Update the route query parameter with the new tab type
    // Only keep q, page, and type parameters when switching tabs
    router.push({
      query: {
        ...route.query,
        type: selectedTab.type,
        page: '1'
      }
    });

    // If the Products tab is selected, enable minimum loading time and update the Products store
    if (selectedTab.type === TabType.PRODUCTS) {
      enableProductsMinTime();
      productsStore.query = searchQuery.value;
      // Initialize filters from query parameters and fetch products
      productsStore.updateFromRouteAndFetch(route.query);
    }

    // Reset resources filter when switching tabs
    if (selectedTab.type !== TabType.RESOURCES) {
      // resourcesStore.resetFilters();
    } else {
      // If switching to Resources tab, update filters from URL
      // resourcesStore.updateFromQueryParams();
    }
  }
});

/**
 * Get the active tab type from route query
 */
const activeTabType = computed<TabType>(() => {
  const queryType = route.query?.type?.toString();
  // Check if the query type matches any of our defined tab types
  const validTab = TABS.find((tab) => tab.type === queryType);
  return validTab ? validTab.type : TabType.PRODUCTS;
});

/**
 * Computed property for products badge count
 */
const productsBadgeCount = computed(() => {
  const val = String(productsStore.products?.maxCount || 0);
  if (
    externalState.tabs.length > TabId.PRODUCTS &&
    externalState.tabs[TabId.PRODUCTS]?.badge
  ) {
    externalState.tabs[TabId.PRODUCTS].badge.text = val;
  }
  return val;
});

/**
 * Computed property for resources badge count
 */
const resourcesBadgeCount = computed(() => {
  // Use dedicated resources search response instead of shared searchResponse
  if (!resourcesSearchResponse.value?.totalCounts) {
    return '0';
  }
  return String(resourcesSearchResponse.value.totalCounts.contentPages || 0);
});

/**
 * Computed property for software badge count
 */
const softwareBadgeCount = computed(() => {
  return String(softwareDownloadStore.softwareDownloads?.maxCount);
});

/**
 * Computed property for documents badge count
 */
const documentsBadgeCount = computed(() => {
  let maxCount = 0;
  if (softwareStore.downloads?.results) {
    for (const key in softwareStore.downloads.results) {
      maxCount = maxCount + softwareStore.downloads.results[key].maxCount;
    }
  }
  return String(maxCount);
});

const searchBoxRef = ref<any | null>(null);
const showResults = ref(false);
const productListIsLoading = ref(false);

// Minimum loading time for skeletons (1.5 seconds)
const MINIMUM_LOADING_TIME = 1000;
const loadingStartTime = ref<number | null>(null);

// Track if we should apply minimum loading time to Products store
const shouldApplyProductsMinTime = ref(false);
const productsLoadingStartTime = ref<number | null>(null);

/**
 * Start loading with minimum time tracking
 */
function startLoadingWithMinTime() {
  productListIsLoading.value = true;
  loadingStartTime.value = Date.now();
}

/**
 * Stop loading with minimum time enforcement
 */
async function stopLoadingWithMinTime() {
  if (loadingStartTime.value) {
    const elapsedTime = Date.now() - loadingStartTime.value;
    const remainingTime = MINIMUM_LOADING_TIME - elapsedTime;

    if (remainingTime > 0) {
      // Wait for the remaining time to reach minimum loading time
      await new Promise((resolve) => setTimeout(resolve, remainingTime));
    }
  }

  productListIsLoading.value = false;
  loadingStartTime.value = null;
}

/**
 * Enable minimum loading time for Products store operations
 */
function enableProductsMinTime() {
  shouldApplyProductsMinTime.value = true;
  productsLoadingStartTime.value = Date.now();
}

/**
 * Disable minimum loading time for Products store operations
 */
function disableProductsMinTime() {
  shouldApplyProductsMinTime.value = false;
  productsLoadingStartTime.value = null;
}

// Watch Products store loading state to apply minimum time when needed
watch(
  () => productsStore.loading,
  async (newLoading, oldLoading) => {
    // When loading starts and we should apply minimum time
    if (newLoading && !oldLoading && shouldApplyProductsMinTime.value) {
      productsLoadingStartTime.value = Date.now();
    }

    // When loading ends and we should apply minimum timfe
    if (
      !newLoading &&
      oldLoading &&
      shouldApplyProductsMinTime.value &&
      productsLoadingStartTime.value
    ) {
      const elapsedTime = Date.now() - productsLoadingStartTime.value;
      const remainingTime = MINIMUM_LOADING_TIME - elapsedTime;

      if (remainingTime > 0) {
        // Keep loading state active for remaining time
        productsStore.loading = true;
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
        productsStore.loading = false;
      }

      // Clean up
      disableProductsMinTime();
    }
  }
);

// Handle filter change from ResourcesFilter component
async function handleResourcesFilterChange() {
  // Show loading state with minimum time
  startLoadingWithMinTime();

  // Reset to page 1 when filters change
  currentPage.value = 1;

  // If we're on the Resources tab, trigger a new search with the selected filters
  if (currentTag.value === TabType.RESOURCES && searchQuery.value.trim()) {
    // Build facet filters from active resource filters
    const facetFilters = resourcesStore.buildFacetFilters();

    // Fetch results with the facet filters
    await fetchResultsWithPagination(searchQuery.value, facetFilters, 0);
  }

  // Hide loading state with minimum time enforcement
  await stopLoadingWithMinTime();
}

// Check if there are resources search results
const hasResourcesResults = computed(() => {
  return (searchResponse.value?.totalCounts?.contentPages ?? 0) > 0;
});

// Filtered results for resources tab
const filteredResourcesResults = computed(() => {
  return getResultsByTag().map((page: any, index: number) => {
    // Calculate position based on current page and index within the page
    // Position is 1-based and represents the product's position in the overall search results
    page.position = (currentPage.value - 1) * 9 + index + 1;
    return page;
  });
});

/**
 * Format active resource filters for display in the TagList
 */
const formattedResourceFilterTags = computed<TagInterface[]>(() => {
  if (!resourcesStore.hasActiveFilters) {
    return [];
  }

  // Create a tag for each active filter
  return resourcesStore.activeFilters.map((filterKey) => {
    // Try to find a label for this filter in the tags.resources array
    const tagInfo = tags.value.resources.find((tag) => tag.value === filterKey);

    let filterText = tagInfo?.label || filterKey;
    if (filterText.includes('##_filter_##')) {
      filterText = filterText.split('##_filter_##')[0];
    }
    const keyName = `searchPage.resourcesFilter.categories.${filterKey}.label`;
    if (keyName in $textDictionary.value) {
      filterText = $textDictionary.value[keyName];
    }

    return {
      text: filterText,
      element: 'button',
      showCloseIcon: true,
      id: filterKey // Store the filter key in the id for reference when clicked
    };
  });
});

/**
 * Handle click on a resource filter tag
 * @param tag The clicked tag
 */
function handleResourceTagClick(tag: TagInterface) {
  if (
    tag.id &&
    (ALLOWED_RESOURCE_TAGS.includes(tag.id) ||
      resourcesStore.availableFacets.includes(tag.id))
  ) {
    // Toggle off the filter
    resourcesStore.setFilter(tag.id, false);
    // Force immediate update of the URL query parameters
    resourcesStore.syncToQueryParams();
    // The route watcher will handle the search, so we don't need to call handleResourcesFilterChange
  }
}

const router = useRouter();
const slug = useSlug();

const {
  searchBoxArgs,
  suggestionGroups,
  onSearchSuggestions,
  tagListGroupArgs
} = useSearchSuggestions();

// region TODO: What is this??? This is never used or?
// Create a mock implementation of the required methods
const technicalComponentMethods = {
  toPlainObject: () => ({
    sys: searchSectionConfig.sys,
    fields: searchSectionConfig.fields,
    metadata: searchSectionConfig.metadata
  }),
  update: () => Promise.resolve(searchSectionConfig)
};

// Define the search section configuration
const searchSectionConfig: ITechnicalComponent = {
  sys: {
    id: 'search-section',
    type: 'Entry',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    locale: locale.value, // Use current locale instead of hardcoded 'en'
    contentType: {
      sys: {
        id: 'technicalComponent',
        linkType: 'ContentType',
        type: 'Link'
      }
    }
  },
  fields: {
    type: 'searchSection',
    internalName: 'Search Section',
    configuration: {
      limit: 10,
      searchIndex: 'basler_website_prod',
      facetAttributes: ['type'],
      sortAttributes: ['_score']
    }
  },
  metadata: {
    tags: []
  },
  ...technicalComponentMethods
};
// endregion TODO: What is this???

const openTabById = (id: string) => {
  externalState.tabs.forEach((tab: TabData) => {
    tab.isActive = tab.computedId === id;
  });
};

/**
 * Handle route query changes and update the active tab and fetch results
 * @param query The current route query
 */
async function handleRouteQueryChange(query: any) {
  // If there's no search query, don't process anything - let the search query watcher handle clearing
  if (!query.q || !query.q.toString().trim()) {
    return;
  }

  // Prevent duplicate calls if already processing
  if (isRouteChangeInProgress.value) {
    return;
  }

  // Set flag to prevent the activeTabNumber watcher from triggering duplicate searches
  isRouteChangeInProgress.value = true;

  // Update search query and related state
  const newValue = String(query.q || '');
  const searchQueryChanged = searchQuery.value !== newValue;
  searchQuery.value = newValue;

  if (newValue) {
    // Add the search query to the search history store
    searchHistoryStore.addSearchEntry(newValue);

    // Show results
    showResults.value = true;

    // Only reset resources filter when search query actually changes
    // Don't reset on filter-only changes to prevent race conditions
    if (searchQueryChanged) {
      // Don't sync to query params since we're already handling a route change
      resourcesStore.resetFiltersWithoutSync();
    }

    autocompleteQuery.value = newValue;
  }

  let typeFromQuery = query.type?.toString();
  let hasAlreadyFetchedResults = false;

  // If there's a search query but no explicit tab type, determine the best tab
  if (query.q && !typeFromQuery) {
    const searchTerm = query.q.toString().trim();
    if (searchTerm) {
      // Determine which tab should be active based on search results
      typeFromQuery = await determineActiveTabWithResults(searchTerm);
      hasAlreadyFetchedResults = true; // Mark that we've already fetched results

      // Update the URL to include the determined tab type
      await router.replace({
        query: {
          ...query,
          type: typeFromQuery
        }
      });
    }
  }

  // Default to PRODUCTS if still no type determined
  if (!typeFromQuery) {
    typeFromQuery = TabType.PRODUCTS;
  }

  // Find the tab that matches the type
  const selectedTab = TABS.find((tab) => tab.type === typeFromQuery);

  // If a matching tab is found
  if (selectedTab) {
    // Get the index of the tab
    // Update the active tab number
    activeTabNumber.value = TABS.findIndex((tab) => tab.type === typeFromQuery);

    // Update the tab component
    openTabById(selectedTab.panelId);

    // Update current tag and active type
    currentTag.value = selectedTab.type;
    activeType.value = selectedTab.type;

    // Only fetch results if we have a search query
    if (searchQuery.value.trim()) {
      showResults.value = true;

      // Get the page from the query or default to 1
      currentPage.value = parseInt(query.page?.toString() || '1', 10);

      // Calculate offset based on current page
      const _offset = (currentPage.value - 1) * ITEMS_PER_PAGE;

      // Handle different tabs differently
      if (currentTag.value === TabType.PRODUCTS) {
        // Enable minimum loading time for Products store
        enableProductsMinTime();

        // For Products tab, use the productsStore
        productsStore.query = searchQuery.value;

        // Only fetch if we haven't already fetched results in determineActiveTabWithResults
        if (!hasAlreadyFetchedResults) {
          // Initialize filters from query parameters and fetch products
          await productsStore.updateFromRouteAndFetch(query);
        }

        // Also update the Resources tab in the background
        const resourcesFacetFilters = {}; // Empty filters for background fetch to get all resources
        fetchResourcesResults(searchQuery.value, resourcesFacetFilters).then(
          (response) => {
            if (response) {
              resourcesSearchResponse.value = response;
              if (
                externalState.tabs.length > 3 &&
                externalState.tabs[3]?.badge
              ) {
                externalState.tabs[3].badge.text = resourcesBadgeCount.value;
              }
            }
          }
        );
      } else if (currentTag.value === TabType.RESOURCES) {
        // For Resources tab, we need to handle pagination differently
        // Only apply minimum loading time if we don't already have search results
        const hasExistingResults =
          resourcesSearchResponse.value?.totalCounts !== undefined;

        if (!hasExistingResults) {
          startLoadingWithMinTime();
        } else {
          // We already have results (even if 0), so just set loading without minimum time
          productListIsLoading.value = true;
        }

        // Build facet filters from active resource filters
        const facetFilters = resourcesStore.buildFacetFilters();

        // Calculate offset based on current page
        const offset = (currentPage.value - 1) * ITEMS_PER_PAGE;

        // Fetch results with the facet filters and calculated offset
        await fetchResultsWithPagination(
          searchQuery.value,
          facetFilters,
          offset
        );

        // Update the dedicated resources search response with the current searchResponse
        resourcesSearchResponse.value = searchResponse.value;

        // Hide loading state - apply minimum time only if we started with minimum time
        if (!hasExistingResults) {
          await stopLoadingWithMinTime();
        } else {
          productListIsLoading.value = false;
        }
      } else {
        // For Software and Documents tabs, check if we already have results for the current search query
        let hasExistingResults = false;

        if (currentTag.value === TabType.SOFTWARE) {
          // Check if we have completed a search for the current search query (regardless of result count)
          hasExistingResults =
            softwareDownloadStore.query === searchQuery.value.trim() &&
            softwareDownloadStore.softwareDownloads?.maxCount !== undefined &&
            !softwareDownloadStore.loading;
        } else if (currentTag.value === TabType.DOCUMENTS) {
          // Check if we have completed a search for the current search query (regardless of result count)
          hasExistingResults =
            softwareStore.downloads?.queryId === searchQuery.value.trim() &&
            softwareStore.downloads?.results !== undefined &&
            !softwareStore.loading;
        }

        // Only show loading skeleton if we don't already have results for this search query
        if (!hasExistingResults) {
          productListIsLoading.value = true;
        }

        // Get the tags for the current tab
        // For Software and Documents tabs, use the static tags
        const currentTags: string[] = tags.value[currentTag.value].map(
          (tag: SearchTagModel) => tag.value
        );

        // For other tabs, use the global search
        // Force activeType to match the current tab
        activeType.value = currentTag.value;

        // Clear any cached results for this query to ensure we get fresh results for the current tab
        // This matches the createCacheKey function in useGlobalSearch.ts
        const cacheKey = `${searchQuery.value.trim()}-${
          Array.isArray(currentTags) ? currentTags.join(',') : currentTags
        }`;

        // Clear the cache for this query
        if (responseCache && responseCache.value) {
          // Clear all cache entries that might match this query with any type
          if (Object.keys(responseCache.value).includes(cacheKey)) {
            delete responseCache.value[cacheKey];
          }
        }

        // Fetch results with the current tags
        await fetchResults(searchQuery.value, currentTags);

        // If we're not on the Resources tab, update it in the background
        if (currentTag.value !== (TabType.RESOURCES as TabType)) {
          const resourcesFacetFilters = {}; // Empty filters for background fetch to get all resources
          fetchResourcesResults(searchQuery.value, resourcesFacetFilters).then(
            (response) => {
              if (response) {
                resourcesSearchResponse.value = response;
                if (
                  externalState.tabs.length > 3 &&
                  externalState.tabs[3]?.badge
                ) {
                  externalState.tabs[3].badge.text = resourcesBadgeCount.value;
                }
              }
            }
          );
        }

        // Only set loading to false if we started loading
        if (!hasExistingResults) {
          productListIsLoading.value = false;
        }
      }
    }
  }

  // Clear the flag to allow normal tab switching behavior
  // Use nextTick to ensure all reactive updates are complete
  await nextTick();
  isRouteChangeInProgress.value = false;
}

// Note: Search query changes are now handled entirely by handleRouteQueryChange
// to prevent duplicate searches. The route.query.q watcher has been removed.

/**
 * Watch for route query changes and update the active tab and fetch results
 */
const routeQueryWatcher = watch(() => route.query, handleRouteQueryChange);

/**
 * Watch for changes in resources store filters and trigger search
 */
watch(
  () => resourcesStore.activeFilters,
  (newFilters, oldFilters) => {
    // Only trigger search if filters actually changed and we have a search query
    if (searchQuery.value.trim() && currentTag.value === TabType.RESOURCES) {
      // Check if filters actually changed
      const filtersChanged =
        JSON.stringify(newFilters) !== JSON.stringify(oldFilters);
      if (filtersChanged) {
        handleResourcesFilterChange();
      }
    }
  },
  { deep: true }
);

const debouncedSuggestionsRequest = debounce(() => {
  onSearchSuggestions(autocompleteQuery.value);
}, 300);

watch(searchQuery, async (query: string) => {
  const newValue = String(query || '').trim();
  if (newValue === '') {
    // Reset all active filters from all stores
    productsStore.resetFilters();
    resourcesStore.resetFilters();
    softwareDownloadStore.resetFilters();
    softwareStore.resetFilters();

    // Clear all query parameters completely
    // await router.push({query: {}});
    // activeTabNumber.value = -1
    await router.push({
      path: route.path,
      query: {}
    });
    return;
  }
  if (newValue.toString().trim().length < MIN_AUTOCOMPLETE_LENGTH) {
    suggestionGroups.value = [];
    return;
  }
  debouncedSuggestionsRequest();
});

watch(autocompleteQuery, async (query: string) => {
  const newValue = String(query || '').trim();

  if (newValue === '') {
    // Clear search query to trigger the main clearing logic
    searchQuery.value = '';
    suggestionGroups.value = [];
    return;
  }

  if (newValue.toString().trim().length < MIN_AUTOCOMPLETE_LENGTH) {
    suggestionGroups.value = [];
    return;
  }
  debouncedSuggestionsRequest();
});

onMounted(() => {
  // Initialize search query from URL
  searchQuery.value = route.query.q?.toString() ?? '';

  // Set focus on search box
  setFocus();

  // Initialize resources store from URL parameters
  resourcesStore.updateFromQueryParams();

  // Note: Products store initialization is handled in handleRouteQueryChange

  // Manually trigger the route watcher to handle initial state
  // This ensures the correct tab is selected and results are fetched
  handleRouteQueryChange(route.query);
});

/**
 * Clean up resources when component is unmounted
 */
onUnmounted(() => {
  // Stop the route watcher to prevent memory leaks
  if (routeQueryWatcher) {
    routeQueryWatcher();
  }
  softwareStore.query = '';
  softwareDownloadStore.query = '';
});

/**
 * Fetch results for all tabs to determine which tabs have results
 * @param searchTerm The search term to fetch results for
 * @returns Promise with results for all tabs
 */
async function fetchAllTabResults(searchTerm: string) {
  const trimmedQuery = searchTerm.trim();
  if (!trimmedQuery) {
    return null;
  }

  try {
    // Set the query for each store before fetching
    productsStore.query = trimmedQuery;
    softwareDownloadStore.query = trimmedQuery;
    softwareStore.query = trimmedQuery;

    // Fetch results for all tab types simultaneously
    const [
      productsResults,
      softwareResults,
      documentsResults,
      resourcesResults
    ] = await Promise.allSettled([
      // Products - use the products store
      productsStore.fetchProducts(),

      // Software - use the software download store
      softwareDownloadStore.fetchSoftwareDownloads(),

      // Documents - use the software store for downloads
      softwareStore.fetchDownloads(),

      // Resources - use our dedicated resources fetch function
      fetchResourcesResults(trimmedQuery, {})
    ]);

    return {
      products:
        productsResults.status === 'fulfilled' ? productsResults.value : null,
      software:
        softwareResults.status === 'fulfilled' ? softwareResults.value : null,
      documents:
        documentsResults.status === 'fulfilled' ? documentsResults.value : null,
      resources:
        resourcesResults.status === 'fulfilled' ? resourcesResults.value : null
    };
  } catch (error) {
    console.error('Error fetching results for all tabs:', error);
    return null;
  }
}

/**
 * Determine which tab should be active based on search results.
 * If Products tab has results, it takes priority. Otherwise, select the tab with the most results.
 * @param searchTerm The search term to check results for
 * @returns The tab type that should be active, or null if no results
 */
async function determineActiveTabWithResults(
  searchTerm: string
): Promise<TabType | null> {
  const trimmedQuery = searchTerm.trim();
  if (!trimmedQuery) {
    return null;
  }

  // Fetch all tab results
  const results = await fetchAllTabResults(trimmedQuery);

  if (!results) {
    return TabType.PRODUCTS; // Default to first tab if fetch fails
  }

  // Update the resources search response if we got results
  if (results.resources) {
    resourcesSearchResponse.value = results.resources;
  }

  // Get result counts for each tab
  const tabsWithCounts = [
    {
      type: TabType.PRODUCTS,
      count: productsStore.products?.maxCount ?? 0
    },
    {
      type: TabType.SOFTWARE,
      count: softwareDownloadStore.softwareDownloads?.maxCount ?? 0
    },
    {
      type: TabType.DOCUMENTS,
      count: (() => {
        if (!softwareStore.downloads?.results) {
          return 0;
        }
        let totalCount = 0;
        for (const key in softwareStore.downloads.results) {
          totalCount += softwareStore.downloads.results[key].maxCount;
        }
        return totalCount;
      })()
    },
    {
      type: TabType.RESOURCES,
      count: resourcesSearchResponse.value?.totalCounts?.contentPages ?? 0
    }
  ];

  // Check if Products tab has results - if so, prioritize it
  const productsTab = tabsWithCounts.find((tab) => {
    return tab.type === TabType.PRODUCTS;
  });
  if (productsTab && productsTab.count > 0) {
    return TabType.PRODUCTS;
  }

  // If no product results, find the tab with the highest result count
  let maxCount = 0;
  let selectedTab = TabType.PRODUCTS; // Default fallback

  for (const tab of tabsWithCounts) {
    if (tab.count > maxCount) {
      maxCount = tab.count;
      selectedTab = tab.type;
    }
  }

  // If no tabs have results, return the first tab (Products) as default
  return selectedTab;
}

/**
 * Handle search form submission
 * @param searchTerm The search term to submit
 */
const onSubmit = async (searchTerm: string) => {
  const trimmedSearchTerm = searchTerm.trim();
  if (!trimmedSearchTerm) {
    return;
  }

  // Don't set searchQuery.value here - let handleRouteQueryChange handle it
  // to prevent duplicate reactive triggers

  // Preserve existing type parameter if it exists, otherwise let route watcher determine it
  const targetType = route.query.type?.toString();

  // Update the URL with the new search term
  // If no type parameter exists, the route watcher will determine the best tab
  await router.push({
    query: {
      ...route.query,
      q: trimmedSearchTerm,
      ...(targetType && { type: targetType }), // Only include type if it exists
      page: '1' // Reset to first page on new search
    }
  });
};

const onDeleteSuggestions = (item: any) => {
  // Check if this is a search history item (has isDeletable property)
  if (item && item.isDeletable) {
    // Find the search history entry with the matching title
    const searchHistoryEntry = searchHistoryStore.recentSearches.find(
      (entry) => entry.id === item.id
    );

    // If found, remove it from the search history
    if (searchHistoryEntry) {
      searchHistoryStore.removeSearchEntry(searchHistoryEntry.id);

      // Refresh the suggestions to update the UI
      if (searchQuery.value.trim().length >= MIN_AUTOCOMPLETE_LENGTH) {
        onSearchSuggestions(searchQuery.value);
      }
    }
  }
};

const searchDropdownIsOpen = computed(() => {
  return searchBoxRef.value?.searchDropdownIsOpen;
});

const setFocus = () => {
  searchBoxRef.value?.focus();
};

// Add schema.org search action for homepage
if (
  $globalPageSettings.value?.searchResultsPage?.slug &&
  $globalPageSettings.value?.searchResultsPage?.slug == slug.value
) {
  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          url: buildUrlString(locale.value, '', undefined, undefined, true),
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: buildUrlString(
                locale.value,
                $globalPageSettings.value?.searchResultsPage.slug,
                'q={search_term_string}',
                undefined,
                true
              )
            },
            'query-input': 'required name=search_term_string'
          }
        })
      }
    ]
  });
}
</script>

<style scoped>
.pagination-container {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
}
</style>
